import { ColumnsType } from 'antd/lib/table'
import {
  ClientDashboardConfig,
  DASHBOARDS,
  DashboardCustomFilters,
  DashboardList,
} from '../dashboard/dashboardCommons'
import { ActionPlansConfig, OmoikaneConfig } from '../omoikane/omoikaneCommons'
import { ReactNode } from 'react'
import { SurveysCountInfo } from '@modules/AppDataModule'

export enum FILTER_TYPES {
  DATES = 'dates',
  LOGICALS = 'logicalDistributions',
  GEOS = 'geographicDistributions',
  CRITICAL_MOMENTS = 'criticalMoments',
  CLUSTERS = 'clusters',
  CHANNELS = 'channels',
  DYNAMIC = 'dynamic',
  TIME_BAND = 'formattedTimeBand',
}

// Opcion de franja horaria (Matutino/Vespertino/Nocturno). El value debe matchear
// literal lo que devuelve la funcion SQL formatted_time_band() en el backend.
export interface TimeBandOption {
  value: string
  label: string
}

// Formulas soportadas para agrupar los valores crudos de una pregunta de dynamic filter
// en categorías (ej. NPS: valores 0-10 -> Promotores/Neutros/Detractores).
export type DynamicFilterGroupFormula = 'nps-10' | 'nps-5' | 'custom'

export interface DynamicFilterGroupConfig {
  // Debe matchear el questionId que manda el backend en configurationUi.metadataFilters
  questionId: number
  formula: DynamicFilterGroupFormula
  // Claves canónicas por categoría, mismo orden que categoriesLabel. Default: ['promoters', 'passives', 'detractors']
  categories?: string[]
  // Labels que ve el usuario en el Select, mismo orden que categories. Default: ['Promotores', 'Neutros', 'Detractores']
  categoriesLabel?: string[]
  // Requerido cuando formula === 'custom'; opcional para pisar los rangos default de 'nps-10'/'nps-5'
  ranges?: Record<string, (string | number)[]>
}

export enum METADADATA_LOAD {
  LOGICALS = 'logicalDistributions',
  GEOS = 'geographicDistributions',
  CRITICAL_MOMENTS = 'criticalMoments',
  CHANNELS = 'channels',
  CLUSTERS = 'clusters',
  DYNAMIC_REPORT_FIELDS = 'dynamicReportsFields',
  WORKFLOWS = 'workflows',
}

export interface RatingRanges {
  defaultRatingMin: number
  defaultRatingMax: number
}

export enum SurveyResponsesTab {
  ANSWERED = 'ANSWERED',
  NOT_ANSWERED = 'NOT_ANSWERED',
  INVALID = 'INVALID',
  IN_PROGRESS = 'IN_PROGRESS'
}

export interface SuveyResponsesIndicatorsConfig {
  nps?: string
  csatGeneral?: string
}

export interface SuveyResponsesConfig {
  url: string
  tableColumns: ColumnsType<any> | undefined
  tabs: SurveyResponsesTab[]
  type: 'post' | 'get' | 'post-context'
  indicators?: SuveyResponsesIndicatorsConfig,
  surveysCountersValue?: (countersInfo: SurveysCountInfo) => number
  surveyDetailFields?: SurveyDetailField[],  
}

export interface SurveyResponseFilterFields{
    enabled?: boolean
    clientName?: boolean,
    customerServiceCode?: boolean
}

export interface SurveyResponseStats {
    showClientMode: boolean,
    clientModeMapping?: (value: string) => string,
    surveyResponseFilterFields?: SurveyResponseFilterFields
}

export interface AlertStats {
    mapGeosPatentChild?: Record<string,number[]>
    showTotalBars?: boolean
    showTotalWidget?: boolean
    colors?: Record<string,string>
    showPercentageBars?: boolean
    useDynamicPadding?: boolean
    labelTextDominantBaseline?: any
        
}

export interface SurveyDetailField {
  position?: number  
  label: string
  getValue: (data: any) => string
}

export interface AlertDetailField {
  position?: number  
  label: string
  getValue: (data: any) => string
}

export interface AlertHeaderField {
  id: number
  dataIndex: string,
  key: string,
  title: string,
  width: number,
  enableSearch?: boolean,
  align?: 'left' | 'right' | 'center' | undefined,
  render?: (value: any, record: any) => string
}
export interface AlertDetailActions{
    reopen?: boolean
}

export interface AlertFilterFields{
    enabled?: boolean
    alertId?: boolean,
    surveyResponseId?: boolean
    customerServiceCode?: boolean
}

export interface AlertsConfig {
  alertDetailFields?: AlertDetailField[],
  alertHeaderFields?: AlertHeaderField[],
  useAlertWorkflows?: boolean,
  alertDetailActions?: AlertDetailActions, 
  alertFilterFields?: AlertFilterFields
}

export interface NotificationsConfig {
  enabled: boolean
}

export interface HelpDefinition {
  alertDetail?: ReactNode
}

export interface QuestionOrder {
  questionName: String
  order: number
}

export interface TrendIcon{
    upTrendIcon?: string,
    downTrendIcon?: string
    
}

export interface AssistantConfig{
    enable: boolean,
    buttonText: string,
    tenant:string
}

export interface ClientConfigInfo {
  // General
  vocBackendUrl: string
  lcmBackendUrl: string
  caseBackendUrl?: string
  publicUrl: string | undefined
  twoAuthFactor?: boolean
  logoSmall: any
  logo: any
  supportsInternationalization: boolean
  decimals: number | undefined
  ratingRanges?: RatingRanges
  help: HelpDefinition
  // Filters
  filtersFromDateSubstract: number
  filtersToDateSubstract: number
  filters: FILTER_TYPES[]
  dynamicFilterGroups?: DynamicFilterGroupConfig[]
  timeBandOptions?: TimeBandOption[]
  dateFromParamName?: string
  dateToParamName?: string
  // Dashboard
  dashboards: ClientDashboardConfig
  dashboardCustomFilters?: DashboardCustomFilters
  menuDashboards: DASHBOARDS[]
  useBackDashboards: boolean
  staticDashboards: DashboardList
  dynamicDashboards?: number[],
  sentimentCommentsIndicatorName: string
  useDefaultCriticalMoment?: boolean
  useDashboardDynamicPosition?: boolean
  trendIcon?: TrendIcon | undefined
  showDownloadDashboardButton?: boolean
  // Metadata
  metadata: METADADATA_LOAD[]
  criticalMomentsRootId: number
  criticalMomentFilterType: 'select' | 'tree'
  criticalMomentMultipleSelect?: boolean,
  // Por default el sidebar oculta los dynamic filters cuando el critical moment
  // seleccionado es el root (criticalMomentsRootId). En true, se calculan también para el root.
  showDynamicFiltersOnCriticalMomentsRoot?: boolean
  showUsersHierarchy?: boolean,
  useSentimentCategories?: boolean
  useFlatGeoLocations?: boolean
  useKeyPressGeoLocations?: boolean
  geosRootId: number
  logicalsRootId: number
  getFilterCriticalMoments?: (
    filtersData: Record<string, any>,
    userProfiles: UserProfile[],
    selectType: 'tree' | 'select' 
  ) => CriticalMoment[]
  getFilterCriticalMomentDefault?: (
    filtersData: Record<string, any>,
    userProfiles: UserProfile[]
  ) => number
  // Reports
  msExcelReportExt: string
  // Omoikane
  omoikane: OmoikaneConfig
  // Action Plan
  actionPlans: ActionPlansConfig,
  // Survey Responses
  surveyResponses: SuveyResponsesConfig,
  // Alert
  alerts: AlertsConfig,
  // Notifications
  notificationsConfig?: NotificationsConfig
  // Customer data info
  userPhoneNumber?: boolean
  pageSize?: number
  questionsCustomerName?: string[]
  questionsOrder?: QuestionOrder[]
  surveyResponseStats?: SurveyResponseStats
  alertStats?: AlertStats
  assistantConfig?:AssistantConfig

}

